import useEcho from "@/hooks/use-echo.hook.ts";
import { CircleChevronRight } from "@/icons/CircleChevronRight.tsx";
import { getPhotoDownloadUrl } from "@/lib/helpers.ts";
import { CreditsCard } from "@/pages/dashboard/CreditsCard.tsx";
import { GettingStartedCard } from "@/pages/dashboard/GettingStartedCard.tsx";
import { SubscriptionCard } from "@/pages/dashboard/SubscriptionCard.tsx";
import { UserCard } from "@/pages/dashboard/UserCard.tsx";
import { getPhotos } from "@/services/photo.service.ts";
import useUserStore from "@/store/user.store.ts";
import { PhotoGrid } from "@/ui/shared/PhotoGrid.tsx";
import { PhotoLightBox } from "@/ui/shared/PhotoLightBox.tsx";
import { Templates } from "@/ui/shared/Templates.tsx";
import { Button, Divider, ScrollArea, Title } from "@mantine/core";
import { useDisclosure, useViewportSize } from "@mantine/hooks";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Link } from "react-router";

export default function Dashboard() {
  const { ref, inView } = useInView();
  const { height, width } = useViewportSize();
  const [viewerOpened, { open: openViewer, close: closeViewer }] =
    useDisclosure(false);
  const [activePhotoIndex, setActivePhotoIndex] = useState<number | null>(null);
  const echo = useEcho();
  const { webSocketsUserId } = useUserStore();

  useEffect(() => {
    if (echo && webSocketsUserId) {
      echo
        .private(`playground.${webSocketsUserId}`)
        .listen("PhotosUpdated", () => {
          refetch();
        });
    }

    return () => {
      if (echo && webSocketsUserId) {
        echo
          .private(`playground.${webSocketsUserId}`)
          .stopListening("PhotosUpdated");
      }
    };
  }, [echo, webSocketsUserId]);

  const { data, isFetching, fetchNextPage, hasNextPage, refetch, isError } =
    useInfiniteQuery({
      retryOnMount: false,
      retry: 1,
      queryKey: [
        "photos",
        {
          limit: 20,
          sort: "-created_at",
          include: "lora",
        },
      ],
      queryFn: (settings) => {
        return getPhotos({
          limit: 20,
          sort: "-created_at",
          page: settings.pageParam,
          "filter[type]": "output",
          include: "lora",
        });
      },
      initialPageParam: 1,
      getPreviousPageParam: (response) => {
        const meta = response.meta;

        if (meta.current_page === 1) {
          return null;
        }
        return meta.current_page - 1;
      },
      getNextPageParam: (response) => {
        const meta = response.meta;
        if (meta.current_page === meta.last_page) {
          return null;
        }
        return meta.current_page + 1;
      },
    });

  const slides = useMemo(() => {
    return (
      data?.pages
        .map((page) => page.data.map((photo) => photo))
        .flat(1)
        .map((photo) => ({
          src: photo.url,
          downloadUrl: getPhotoDownloadUrl(photo),
          description: photo.prompt,
        })) || []
    );
  }, [data]);

  const handlePhotoClick = useCallback(
    (pageIndex: number, photoIndex: number) => {
      openViewer();
      const index = pageIndex * 20 + photoIndex;
      setActivePhotoIndex(index);
    },
    [openViewer, setActivePhotoIndex],
  );

  const memoizedData = useMemo(() => data, [data]);

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, hasNextPage]);

  return (
    <>
      <div className="flex flex-col gap-5">
        <div className="relative grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-y-4 lg:gap-x-4">
          <div className="col-span-1 relative">
            <div
              style={{
                top: "4.75rem",
              }}
              className="sticky"
            >
              <ScrollArea
                h={width > 1024 ? height - 140 : "100%"}
                scrollbarSize={4}
                scrollbars="y"
                type="hover"
                offsetScrollbars={true}
              >
                <div className="flex flex-col gap-4">
                  <UserCard />
                  <GettingStartedCard />
                  <CreditsCard />
                  <SubscriptionCard />
                </div>
              </ScrollArea>
            </div>
          </div>

          <div className="col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-3 2xl:col-span-4 flex flex-col w-full gap-5 md:gap-10">
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center">
                  <Title order={3} fw={500}>
                    Explore
                  </Title>

                  <Button
                    component={Link}
                    to="/explore"
                    size="xs"
                    rightSection={<CircleChevronRight />}
                    variant="transparent"
                  >
                    View all
                  </Button>
                </div>

                <Divider />
              </div>

              <Templates />
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <div className="flex justify-between items-center">
                  <Title order={3} fw={500}>
                    Recent photos
                  </Title>

                  <Button
                    component={Link}
                    to="/playground"
                    size="xs"
                    rightSection={<CircleChevronRight />}
                    variant="transparent"
                  >
                    View all
                  </Button>
                </div>

                <Divider />
              </div>

              <PhotoGrid
                data={memoizedData}
                isFetching={isFetching}
                handlePhotoClick={handlePhotoClick}
                isError={isError}
                showAI={false}
                inViewRef={ref}
              />
            </div>
          </div>
        </div>
      </div>

      <PhotoLightBox
        viewerOpened={viewerOpened}
        activePhotoIndex={activePhotoIndex}
        closeViewer={closeViewer}
        slides={slides}
      />
    </>
  );
}
